'use strict';

/**
 * Event target base class used for custom events system.
 *
 * @class Bindable
 * @constructor
 * @module Core
 */
export class Bindable {
    _listeners = {};

    /**
     * Adds a new listener of given type.
     *
     * @method bind
     * @param {String|Array} type Type of listener to add (or array of them)
     * @param {Function} listener The listener function to add
     * @return {Object} Containing the type, listener and unbind method
     */
    bind(type, listener) {
        if (typeof(type) === 'string') {
            type = [type];
        }

        var self = this,
            i;

        for (i = 0; i < type.length; i++) {
            // first of given type, create array
            if (typeof(this._listeners[type[i]]) === 'undefined') {
                this._listeners[type[i]] = [];
            }

            // check for an already existing listener for the same type
            for (var j = 0; j < this._listeners[type[i]].length; j++) {
                if (this._listeners[type[i]][j] === listener) {
                    return listener;
                }
            }

            this._listeners[type[i]].push(listener);
        }

        this._onBind(type, listener);

        return {
            type: type,
            listener: listener,
            unbind: function () {
                self.unbind(type, listener);
            }
        };
    };

    /**
     * Called when a new listener is binded.
     *
     * This does noting by default but can be overriden in child class to do something when a listener is added.
     *
     * @method _onBind
     * @param {String} type Type of listener added
     * @param {Function} listener The added listener function
     * @protected
     */
    _onBind(type?, listener?) {
    }

    /**
     * Removes listener of given type.
     *
     * If no type is given, all listeners are removed. If no listener is given, all listeners of given type are removed.
     *
     * @method unbind
     * @param {String|Array} [type] Type of listener to remove (or array of them)
     * @param {Function} [listener] The listener function to remove
     * @return {Boolean} Was unbinding the event successful
     */
    unbind(type, listener) {
        var i;

        if (typeof(type) === 'undefined') {
            this._listeners = {};

            return true;
        } else if (typeof(type) === 'object' && typeof(type.length) === 'number') {
            var result = true;

            for (i = 0; i < type.length; i++) {
                if (!this.unbind(type[i], listener)) {
                    result = false;
                }
            }

            return result;
        }

        if (typeof(listener) === 'undefined') {
            this._listeners[type] = [];

            return true;
        }

        // give up if none or requested type exist
        if (typeof(this._listeners[type]) === 'undefined') {
            return false;
        }

        // find it
        for (i = 0; i < this._listeners[type].length; i++) {
            if (this._listeners[type][i] === listener) {
                // splice it out of the array
                this._listeners[type].splice(i, 1);

                return true;
            }
        }

        return false;
    }

    /**
     * Fire event
     *
     * The event can be a simple string meaning the event type to fire or
     * an object containing type as key and optionally a target. If no
     * target is given, the current context is used.
     *
     * @method fire
     * @param {Object} event Event to fire
     */
    fire(event) {
        if (typeof(event) === 'string') {
            event = {
                type: event
            };
        }

        if (typeof(event.target) === 'undefined') {
            event.target = this;
        }

        if (typeof(event.type) === 'undefined') {
            throw 'Event "type" attribute is missing';
        }

        var propagate = true;

        if (typeof(this._listeners[event.type]) === 'object') {
            for (var i = 0; i < this._listeners[event.type].length; i++) {
                if (this._listeners[event.type][i].call(this, event) === false) {
                    propagate = false;
                }
            }
        }

        return propagate;
    }

    /**
     * Returns the number of event listeners of given type.
     *
     * @method numListeners
     * @param {String} type Type of listener to add
     * @return {Number}
     */
    numListeners(type) {
        if (typeof(this._listeners[type]) === 'undefined') {
            return 0;
        }

        return this._listeners[type].length;
    }

    /**
     * Clears all listeners of a type or all if no type is given
     *
     * @method clearListeners
     * @param {String} type Type to clear, leave empty for all
     */
    clearListeners(type) {
        if (typeof(type) === 'undefined') {
            this._listeners = {};
        } else {
            this._listeners[type] = [];
        }
    }

    /**
     * Returns list of all listeners.
     *
     * If type is given, returns listeners for it, if not, returns all listeners
     * for all types.
     *
     * @method getListeners
     * @param {String} [type] Optional type
     * @return {object} Listeners
     */
    getListeners(type) {
        if (typeof(type) === 'string') {
            if (typeof(this._listeners[type]) !== 'undefined') {
                return this._listeners[type];
            } else {
                return null;
            }
        }

        return this._listeners;
    };
}




// WEBPACK FOOTER //
// ./src/main/ui/core/application/Bindable.ts